/**
 * Honest IELTS catalog: one id = one unique module.
 * New modules use /exams/english/lesson/:courseId.
 * Existing modules keep their legacy paths for compatibility.
 */

import mockExamData from '../assets/mock-data/mockexam.json';

const lesson = ({
  id,
  name,
  path,
  component,
  difficulty,
  description = '',
  academic = false,
  preIelts = false,
  cefr = null,
  format = null,
}) => ({
  id,
  courseId: id,
  name,
  title: name,
  description: description || name,
  path: path || `/exams/english/lesson/${id}`,
  component: component || 'DynamicLesson',
  difficulty,
  academic,
  preIelts,
  cefr,
  format,
});

export const GRAMMAR_LESSONS = [
  lesson({
    id: 'sentence-structure',
    name: 'Sentence Structure',
    path: '/exams/english/grammar/sentence-structure',
    component: 'SentenceStructureTips',
    difficulty: 1,
    preIelts: true,
    description: 'Build grammatically correct sentences',
  }),
  lesson({
    id: 'subject-verb-agreement',
    name: 'Subject-Verb Agreement',
    path: '/exams/english/grammar/subject-verb-agreement',
    component: 'SubjectVerbAgreementTips',
    difficulty: 1,
    preIelts: true,
    description: 'Make subjects and verbs agree',
  }),
  lesson({
    id: 'present-simple',
    name: 'Present Simple Tense',
    difficulty: 1,
    preIelts: true,
    description: 'Habits, facts and routines',
  }),
  lesson({
    id: 'present-continuous',
    name: 'Present Continuous Tense',
    difficulty: 1,
    preIelts: true,
    description: 'Actions happening now',
  }),
  lesson({
    id: 'articles-basics',
    name: 'Articles (a, an, the)',
    difficulty: 1,
    preIelts: true,
    description: 'Choose the right article',
  }),
  lesson({
    id: 'basic-prepositions',
    name: 'Basic Prepositions',
    difficulty: 1,
    preIelts: true,
    description: 'in, on, at, to, from',
  }),
  lesson({
    id: 'countable-uncountable',
    name: 'Countable & Uncountable Nouns',
    difficulty: 1,
    preIelts: true,
    description: 'much, many, a few, a little',
  }),
  lesson({
    id: 'comparatives-superlatives',
    name: 'Comparatives & Superlatives',
    difficulty: 1,
    preIelts: true,
    description: 'Compare people and things',
  }),
  lesson({
    id: 'gerunds',
    name: 'Gerunds',
    path: '/exams/english/grammar/gerunds',
    component: 'GerundsTips',
    difficulty: 2,
    preIelts: true,
  }),
  lesson({
    id: 'infinitives',
    name: 'Infinitives',
    path: '/exams/english/grammar/infinitives',
    component: 'InfinitiveTips',
    difficulty: 2,
    preIelts: true,
  }),
  lesson({
    id: 'gerunds-vs-infinitives',
    name: 'Gerunds vs Infinitives',
    path: '/exams/english/grammar/gerund-infinitive',
    component: 'GerundsVsInfinitivesTips',
    difficulty: 2,
    preIelts: true,
  }),
  lesson({
    id: 'past-simple',
    name: 'Past Simple Tense',
    difficulty: 2,
    preIelts: true,
    description: 'Finished actions in the past',
  }),
  lesson({
    id: 'past-continuous',
    name: 'Past Continuous Tense',
    difficulty: 2,
    preIelts: true,
    description: 'Actions in progress in the past',
  }),
  lesson({
    id: 'present-perfect-intro',
    name: 'Present Perfect Introduction',
    difficulty: 2,
    preIelts: true,
    description: 'Have/has + past participle',
  }),
  lesson({
    id: 'future-forms',
    name: 'Future Forms (will / going to)',
    difficulty: 2,
    preIelts: true,
    description: 'Plans and predictions',
  }),
  lesson({
    id: 'conjunctions-basic',
    name: 'Conjunctions (and, but, or, so)',
    difficulty: 2,
    preIelts: true,
    description: 'Join simple ideas',
  }),
  lesson({
    id: 'sentence-clauses',
    name: 'Sentence Clauses',
    path: '/exams/english/grammar/sentence-clauses',
    component: 'SentenceClauseTips',
    difficulty: 3,
  }),
  lesson({
    id: 'passive-voice',
    name: 'Passive Voice',
    path: '/exams/english/grammar/passive-voice',
    component: 'PassiveVoiceTips',
    difficulty: 3,
  }),
  lesson({
    id: 'modal-verbs',
    name: 'Modal Verbs',
    path: '/exams/english/grammar/modal-verbs',
    component: 'ModalVerbsTips',
    difficulty: 3,
  }),
  lesson({
    id: 'first-conditional',
    name: 'First Conditional',
    difficulty: 3,
    description: 'Real future possibilities',
  }),
  lesson({
    id: 'zero-conditional',
    name: 'Zero Conditional',
    difficulty: 3,
    description: 'Facts and general truths',
  }),
  lesson({
    id: 'second-conditional',
    name: 'Second Conditional',
    difficulty: 3,
    description: 'Hypothetical present situations',
  }),
  lesson({
    id: 'reported-speech',
    name: 'Reported Speech',
    difficulty: 3,
    description: 'Report what someone said',
  }),
  lesson({
    id: 'present-perfect-adv',
    name: 'Present Perfect vs Past Simple',
    difficulty: 3,
    description: 'Choose the right past form',
  }),
  lesson({
    id: 'question-tags',
    name: 'Question Tags',
    difficulty: 3,
    description: "You're ready, aren't you?",
  }),
  lesson({
    id: 'relative-clauses',
    name: 'Relative Clauses',
    path: '/exams/english/grammar/relative-clauses',
    component: 'RelativeClauseTips',
    difficulty: 4,
  }),
  lesson({
    id: 'noun-clauses',
    name: 'Noun Clauses',
    path: '/exams/english/grammar/noun-clauses',
    component: 'NounClauseTips',
    difficulty: 4,
  }),
  lesson({
    id: 'adverbial-clauses',
    name: 'Adverbial Clauses',
    path: '/exams/english/grammar/adverbial-clauses',
    component: 'AdverbialClauseTips',
    difficulty: 4,
  }),
  lesson({
    id: 'third-conditional',
    name: 'Third Conditional',
    difficulty: 4,
    description: 'Imaginary past results',
  }),
  lesson({
    id: 'mixed-conditionals',
    name: 'Mixed Conditionals',
    difficulty: 4,
    description: 'Past cause, present result',
  }),
  lesson({
    id: 'inversion',
    name: 'Inversion for Emphasis',
    difficulty: 4,
    description: 'Never have I seen…',
  }),
  lesson({
    id: 'wish-clauses',
    name: 'Wish & If Only Clauses',
    difficulty: 4,
    description: 'Regret and desire',
  }),
  lesson({
    id: 'past-perfect',
    name: 'Past Perfect & Narratives',
    difficulty: 4,
    description: 'The earlier past',
  }),
  lesson({
    id: 'complex-sentences',
    name: 'Complex Sentences',
    path: '/exams/english/grammar/complex-sentences',
    component: 'ComplexSentencesTips',
    difficulty: 5,
  }),
  lesson({
    id: 'parallel-structure',
    name: 'Parallel Structure',
    path: '/exams/english/grammar/parallel-structure',
    component: 'ParallelStructureTips',
    difficulty: 5,
  }),
  lesson({
    id: 'grammar-rules',
    name: 'Advanced Grammar Rules',
    path: '/exams/english/grammar/grammar-rules',
    component: 'GrammarRulesTips',
    difficulty: 5,
  }),
  lesson({
    id: 'cleft-sentences',
    name: 'Cleft Sentences',
    difficulty: 5,
    description: 'It is X that…',
  }),
  lesson({
    id: 'ellipsis-substitution',
    name: 'Ellipsis & Substitution',
    difficulty: 5,
    description: 'Avoid repetition naturally',
  }),
  lesson({
    id: 'subjunctive-mood',
    name: 'Subjunctive Mood',
    difficulty: 5,
    description: 'It is essential that he be…',
  }),
  lesson({
    id: 'advanced-modals',
    name: 'Advanced Modal Patterns',
    difficulty: 5,
    description: 'must have, might have, should have',
  }),
  lesson({
    id: 'nominalization',
    name: 'Nominalization',
    difficulty: 5,
    description: 'Turn verbs into academic nouns',
  }),
];

export const VOCABULARY_LESSONS = [
  lesson({
    id: 'essential-words',
    name: 'Essential Words',
    path: '/exams/english/vocabulary/essential-words',
    component: 'EssentialWordsTips',
    difficulty: 1,
    preIelts: true,
  }),
  lesson({
    id: 'spelling-usuk',
    name: 'US vs UK Spelling',
    path: '/exams/english/vocabulary/spelling-us-uk',
    component: 'SpellingUSUKTips',
    difficulty: 1,
    preIelts: true,
  }),
  lesson({
    id: 'common-verbs',
    name: 'Common Verbs',
    difficulty: 1,
    preIelts: true,
    description: 'High-frequency everyday verbs',
  }),
  lesson({
    id: 'daily-life-vocab',
    name: 'Daily Life Vocabulary',
    difficulty: 1,
    preIelts: true,
    description: 'Home, work and routines',
  }),
  lesson({
    id: 'numbers-dates-time',
    name: 'Numbers, Dates & Time',
    difficulty: 1,
    preIelts: true,
    description: 'Say dates, prices and times',
  }),
  lesson({
    id: 'basic-adjectives',
    name: 'Basic Adjectives',
    difficulty: 1,
    preIelts: true,
    description: 'Describe people and things',
  }),
  lesson({
    id: 'family-people',
    name: 'Family & People Vocabulary',
    difficulty: 1,
    preIelts: true,
    description: 'Family, jobs and appearance',
  }),
  lesson({
    id: 'places-directions',
    name: 'Places & Directions',
    difficulty: 1,
    preIelts: true,
    description: 'Ask for and give directions',
  }),
  lesson({
    id: 'uncountable-nouns',
    name: 'Uncountable Nouns',
    path: '/exams/english/vocabulary/uncountable-nouns',
    component: 'UncountableNounsTips',
    difficulty: 2,
    preIelts: true,
  }),
  lesson({
    id: 'individual-words',
    name: 'Individual Word Study',
    path: '/exams/english/vocabulary/individual-words',
    component: 'IndividualWordsTips',
    difficulty: 2,
    preIelts: true,
  }),
  lesson({
    id: 'word-families',
    name: 'Word Families',
    difficulty: 2,
    preIelts: true,
    description: 'decide, decision, decisive',
  }),
  lesson({
    id: 'linking-words-basic',
    name: 'Basic Linking Words',
    difficulty: 2,
    preIelts: true,
    description: 'and, but, because, so',
  }),
  lesson({
    id: 'food-health-vocab',
    name: 'Food & Health Vocabulary',
    difficulty: 2,
    preIelts: true,
    description: 'Meals, body and wellbeing',
  }),
  lesson({
    id: 'travel-transport',
    name: 'Travel & Transport Vocabulary',
    difficulty: 2,
    preIelts: true,
    description: 'Airports, tickets and trips',
  }),
  lesson({
    id: 'education-vocab-basic',
    name: 'Education Vocabulary Basics',
    difficulty: 2,
    preIelts: true,
    description: 'School, subjects and study',
  }),
  lesson({
    id: 'prefixes-suffixes',
    name: 'Prefixes & Suffixes',
    difficulty: 2,
    preIelts: true,
    description: 'Build words from parts',
  }),
  lesson({
    id: 'collocations',
    name: 'Collocations',
    path: '/exams/english/vocabulary/collocations',
    component: 'CollocationTips',
    difficulty: 3,
  }),
  lesson({
    id: 'phrasal-verbs',
    name: 'Phrasal Verbs',
    path: '/exams/english/vocabulary/phrasal-verbs',
    component: 'PhrasalVerbsTips',
    difficulty: 3,
  }),
  lesson({
    id: 'awl-sublist-1',
    name: 'Academic Word List: Sublist 1',
    difficulty: 3,
    academic: true,
    description: 'Core academic word families',
  }),
  lesson({
    id: 'awl-sublist-2',
    name: 'Academic Word List: Sublist 2',
    difficulty: 3,
    academic: true,
    description: 'Evaluate, strategy, impact',
  }),
  lesson({
    id: 'awl-sublist-3',
    name: 'Academic Word List: Sublist 3',
    difficulty: 3,
    academic: true,
    description: 'Demonstrate, framework, outcome',
  }),
  lesson({
    id: 'topic-vocab-education',
    name: 'Topic Vocabulary: Education',
    difficulty: 3,
    description: 'Schools, degrees and learning',
  }),
  lesson({
    id: 'connectors-transitions',
    name: 'Connectors & Transitions',
    difficulty: 3,
    description: 'however, therefore, in contrast',
  }),
  lesson({
    id: 'idioms',
    name: 'Idioms & Expressions',
    path: '/exams/english/vocabulary/idioms',
    component: 'IdiomsTips',
    difficulty: 4,
  }),
  lesson({
    id: 'topic-vocabulary',
    name: 'Topic-Related Vocabulary',
    path: '/exams/english/vocabulary/topic-related',
    component: 'TopicRelatedVocabularyTips',
    difficulty: 4,
  }),
  lesson({
    id: 'topic-vocab-environment',
    name: 'Topic Vocabulary: Environment',
    difficulty: 4,
    description: 'Climate, energy and nature',
  }),
  lesson({
    id: 'topic-vocab-technology',
    name: 'Topic Vocabulary: Technology',
    difficulty: 4,
    description: 'Devices, data and innovation',
  }),
  lesson({
    id: 'topic-vocab-health',
    name: 'Topic Vocabulary: Health',
    difficulty: 4,
    description: 'Prevention, nutrition and care',
  }),
  lesson({
    id: 'awl-sublist-4',
    name: 'Academic Word List: Sublist 4',
    difficulty: 4,
    academic: true,
    description: 'Investigate, hypothesis, predict',
  }),
  lesson({
    id: 'awl-sublist-5',
    name: 'Academic Word List: Sublist 5',
    difficulty: 4,
    academic: true,
    description: 'Generate, modify, fundamental',
  }),
  lesson({
    id: 'awl-sublist-6',
    name: 'Academic Word List: Sublist 6',
    difficulty: 4,
    academic: true,
    description: 'Enhance, transform, accurate',
  }),
  lesson({
    id: 'awl-sublist-7',
    name: 'Academic Word List: Sublist 7',
    difficulty: 4,
    academic: true,
    description: 'Confirm, infer, comprehensive',
  }),
  lesson({
    id: 'formal-register',
    name: 'Formal vs Informal Register',
    difficulty: 4,
    description: 'Sound academic, not casual',
  }),
  lesson({
    id: 'paraphrasing',
    name: 'Paraphrasing Techniques',
    path: '/exams/english/vocabulary/paraphrasing',
    component: 'ParaphrasingTips',
    difficulty: 5,
  }),
  lesson({
    id: 'vocabulary-writing',
    name: 'Vocabulary for Writing',
    path: '/exams/english/vocabulary/vocabulary-for-writing',
    component: 'VocabularyForWriting',
    difficulty: 5,
    academic: true,
  }),
  lesson({
    id: 'hedging-language',
    name: 'Hedging Language',
    difficulty: 5,
    description: 'Tentative academic claims',
  }),
  lesson({
    id: 'awl-sublist-8',
    name: 'Academic Word List: Sublist 8',
    difficulty: 5,
    academic: true,
    description: 'Highlight, contradict, crucial',
  }),
  lesson({
    id: 'awl-sublist-9',
    name: 'Academic Word List: Sublist 9',
    difficulty: 5,
    academic: true,
    description: 'Anticipate, coherent, integral',
  }),
  lesson({
    id: 'awl-sublist-10',
    name: 'Academic Word List: Sublist 10',
    difficulty: 5,
    academic: true,
    description: 'Albeit, persist, undergo',
  }),
  lesson({
    id: 'less-common-vocab',
    name: 'Less Common Vocabulary',
    difficulty: 5,
    cefr: ['C2'],
    description: 'Precise, low-frequency lexis',
  }),
  lesson({
    id: 'nuanced-expressions',
    name: 'Nuanced Expressions',
    difficulty: 5,
    cefr: ['C2'],
    description: 'Fine shades of meaning',
  }),
  lesson({
    id: 'awl-sublist-4-5',
    name: 'Academic Word List: Sublists 4-5',
    difficulty: 4,
    academic: true,
    description: 'Intermediate academic families',
  }),
  lesson({
    id: 'awl-sublist-6-7',
    name: 'Academic Word List: Sublists 6-7',
    difficulty: 4,
    academic: true,
    description: 'Less frequent academic families',
  }),
  lesson({
    id: 'awl-sublist-8-10',
    name: 'Academic Word List: Sublists 8-10',
    difficulty: 5,
    academic: true,
    description: 'Low-frequency academic families',
  }),
  lesson({
    id: 'academic-collocations',
    name: 'Academic Collocations',
    difficulty: 4,
    academic: true,
    description: 'Words that naturally go together',
  }),
  lesson({
    id: 'sophisticated-connectors',
    name: 'Sophisticated Connectors',
    difficulty: 5,
    academic: true,
    description: 'Precise academic linking',
  }),
  lesson({
    id: 'synonyms-antonyms',
    name: 'Synonyms & Antonyms',
    difficulty: 3,
    description: 'Paraphrase with precise opposites',
  }),
];

export const IELTS_SKILLS_COURSES = {
  listening: [
    lesson({
      id: 'listening-strategies',
      name: 'Listening Strategies',
      path: '/exams/english/courses/listening-strategies',
      component: 'ListeningStrategies',
      difficulty: 1,
      preIelts: true,
      description: 'Key strategies for listening',
    }),
    lesson({
      id: 'everyday-conversations',
      name: 'Everyday Conversations',
      difficulty: 1,
      preIelts: true,
      description: 'Social and everyday dialogues',
    }),
    lesson({
      id: 'following-directions',
      name: 'Following Directions & Maps',
      difficulty: 2,
      preIelts: true,
      description: 'Understand spoken directions',
    }),
    lesson({
      id: 'understanding-numbers',
      name: 'Understanding Numbers & Spelling',
      difficulty: 2,
      preIelts: true,
      description: 'Catch numbers, dates and spelled words',
    }),
    lesson({
      id: 'note-taking',
      name: 'Note-Taking Skills',
      path: '/exams/english/courses/note-taking',
      component: 'NoteTakingSkills',
      difficulty: 3,
      academic: true,
      description: 'Notes during listening',
    }),
    lesson({
      id: 'listening-for-details',
      name: 'Listening for Details',
      path: '/exams/english/courses/listening-details',
      component: 'ListeningForDetails',
      difficulty: 3,
      description: 'Catch specific information',
    }),
    lesson({
      id: 'accent-training',
      name: 'Accent Training',
      path: '/exams/english/courses/accent-training',
      component: 'AccentTraining',
      difficulty: 4,
      description: 'Different English accents',
    }),
    lesson({
    id: 'paraphrase-detection',
    name: 'Paraphrase Detection',
    difficulty: 5,
    academic: true,
    description: 'Hear the same idea in new words',
    }),
    lesson({
      id: 'prediction-skills',
      name: 'Prediction Skills',
      difficulty: 3,
      academic: true,
      description: 'Predict answers before you hear them',
    }),
    lesson({
      id: 'multiple-speakers',
      name: 'Multiple Speakers',
      difficulty: 4,
      academic: true,
      description: 'Follow conversations with several voices',
    }),
    lesson({
      id: 'academic-lectures',
      name: 'Academic Lectures',
      difficulty: 4,
      academic: true,
      description: 'Follow lecture structure and signposting',
    }),
    lesson({
      id: 'distractor-recognition',
      name: 'Distractor Recognition',
      difficulty: 5,
      academic: true,
      description: 'Ignore false leads in the recording',
    }),
  ],
  reading: [
    lesson({
      id: 'skimming-scanning',
      name: 'Skimming & Scanning',
      path: '/exams/english/courses/skimming-scanning',
      component: 'SkimmingScanning',
      difficulty: 1,
      preIelts: true,
      description: 'Speed reading techniques',
    }),
    lesson({
      id: 'keyword-identification',
      name: 'Keyword Identification',
      difficulty: 1,
      preIelts: true,
      description: 'Find key words in questions',
    }),
    lesson({
      id: 'tfng-basics',
      name: 'True/False/Not Given Basics',
      difficulty: 2,
      preIelts: true,
      description: 'Understand TFNG questions',
    }),
    lesson({
      id: 'matching-headings',
      name: 'Matching Headings',
      difficulty: 2,
      preIelts: true,
      description: 'Match headings to paragraphs',
    }),
    lesson({
      id: 'reading-strategies',
      name: 'Reading Strategies',
      path: '/exams/english/courses/reading-strategies',
      component: 'ReadingStrategies',
      difficulty: 3,
      academic: true,
      description: 'IELTS reading question types',
    }),
    lesson({
      id: 'time-management-reading',
      name: 'Time Management',
      path: '/exams/english/courses/reading-time',
      component: 'ReadingTimeManagement',
      difficulty: 3,
      academic: true,
      description: 'Manage time in reading tests',
    }),
    lesson({
      id: 'academic-vocabulary',
      name: 'Academic Vocabulary',
      path: '/exams/english/courses/academic-vocab',
      component: 'AcademicVocabulary',
      difficulty: 4,
      academic: true,
      description: 'Key academic words for reading',
    }),
    lesson({
      id: 'identifying-writer-views',
      name: 'Identifying Writer Views',
      difficulty: 5,
      academic: true,
      description: 'Facts versus opinions',
    }),
    lesson({
      id: 'matching-information',
      name: 'Matching Information',
      difficulty: 3,
      academic: true,
      description: 'Locate details in paragraphs',
    }),
    lesson({
      id: 'diagram-labelling',
      name: 'Diagram Labelling',
      difficulty: 3,
      academic: true,
      description: 'Label diagrams from the passage',
    }),
    lesson({
      id: 'summary-completion',
      name: 'Summary Completion',
      difficulty: 4,
      academic: true,
      description: 'Complete summaries with passage words',
    }),
    lesson({
      id: 'sentence-completion-reading',
      name: 'Sentence Completion',
      difficulty: 4,
      academic: true,
      description: 'Finish sentences from the text',
    }),
    lesson({
      id: 'multiple-choice-advanced',
      name: 'Multiple Choice Advanced',
      difficulty: 5,
      academic: true,
      description: 'Handle tricky reading options',
    }),
    lesson({
      id: 'speed-reading-complex',
      name: 'Speed Reading Complex Texts',
      difficulty: 5,
      academic: true,
      description: 'Read dense academic passages faster',
    }),
  ],
  writing: [
    lesson({
      id: 'basic-paragraph-structure',
      name: 'Basic Paragraph Structure',
      difficulty: 1,
      preIelts: true,
      description: 'Build a clear paragraph',
    }),
    lesson({
      id: 'simple-linking-words',
      name: 'Simple Linking Words',
      difficulty: 2,
      preIelts: true,
      description: 'firstly, moreover, however',
    }),
    lesson({
      id: 'describing-trends',
      name: 'Describing Trends Basics',
      difficulty: 2,
      preIelts: true,
      description: 'Rises, falls and fluctuations',
    }),
    lesson({
      id: 'task1-overview',
      name: 'Task 1 Overview',
      path: '/exams/english/courses/task1-overview',
      component: 'Task1Overview',
      difficulty: 3,
      academic: true,
      description: 'Charts, graphs and diagrams',
    }),
    lesson({
      id: 'task2-essay-structure',
      name: 'Essay Structure',
      path: '/exams/english/courses/essay-structure',
      component: 'EssayStructure',
      difficulty: 3,
      academic: true,
      description: 'Organize Task 2 essays',
    }),
    lesson({
      id: 'coherence-cohesion',
      name: 'Coherence & Cohesion',
      path: '/exams/english/courses/coherence-cohesion',
      component: 'CoherenceCohesion',
      difficulty: 3,
      academic: true,
      description: 'Link ideas effectively',
    }),
    lesson({
      id: 'opinion-essay-framework',
      name: 'Opinion Essay Framework',
      difficulty: 3,
      academic: true,
      description: 'Agree / disagree essays',
    }),
    lesson({
      id: 'writing-band-descriptors',
      name: 'Band Descriptors',
      path: '/exams/english/courses/band-descriptors',
      component: 'BandDescriptors',
      difficulty: 4,
      academic: true,
      description: 'Understand scoring criteria',
    }),
    lesson({
      id: 'discussion-essays',
      name: 'Task 2 Discussion Essays',
      difficulty: 5,
      academic: true,
      description: 'Balanced discussion essays',
    }),
    lesson({
      id: 'task1-data-comparison',
      name: 'Task 1 Data Comparison',
      difficulty: 4,
      academic: true,
      description: 'Compare numbers and categories',
    }),
    lesson({
      id: 'task1-general-letters',
      name: 'Task 1 General Training: Letters',
      difficulty: 3,
      format: 'general',
      description: 'Write formal and informal letters',
    }),
    lesson({
      id: 'advanced-cohesion',
      name: 'Advanced Cohesion Devices',
      difficulty: 5,
      academic: true,
      description: 'Link ideas at Band 8',
    }),
    lesson({
      id: 'complex-sentence-writing',
      name: 'Complex Sentence Patterns for Writing',
      difficulty: 5,
      academic: true,
      description: 'Build controlled complex sentences',
    }),
  ],
  speaking: [
    lesson({
      id: 'fluency-training',
      name: 'Fluency Training',
      path: '/exams/english/courses/fluency',
      component: 'FluencyTraining',
      difficulty: 1,
      preIelts: true,
      description: 'Speak without hesitation',
    }),
    lesson({
      id: 'part1-simple-answers',
      name: 'Part 1 Simple Answers',
      difficulty: 1,
      preIelts: true,
      description: 'Answer personal questions',
    }),
    lesson({
      id: 'self-introduction',
      name: 'Self-Introduction Skills',
      difficulty: 2,
      preIelts: true,
      description: 'Introduce yourself clearly',
    }),
    lesson({
      id: 'pronunciation',
      name: 'Pronunciation Advanced',
      path: '/exams/english/courses/pronunciation',
      component: 'PronunciationCourse',
      difficulty: 3,
      description: 'Connected speech and intonation',
    }),
    lesson({
      id: 'speaking-part2',
      name: 'Part 2 Long Turn',
      path: '/exams/english/courses/speaking-part2',
      component: 'SpeakingPart2',
      difficulty: 3,
      academic: true,
      description: 'Structure 2-minute responses',
    }),
    lesson({
      id: 'extending-answers',
      name: 'Extending Answers',
      difficulty: 3,
      description: 'Add detail, reasons and examples',
    }),
    lesson({
      id: 'speaking-part3',
      name: 'Part 3 Discussion',
      path: '/exams/english/courses/speaking-part3',
      component: 'SpeakingPart3',
      difficulty: 4,
      academic: true,
      description: 'Abstract discussion techniques',
    }),
    lesson({
      id: 'hedging-speculating',
      name: 'Hedging & Speculating',
      difficulty: 5,
      description: 'Tentative spoken language',
    }),
    lesson({
      id: 'advanced-discourse',
      name: 'Advanced Discourse Markers',
      difficulty: 5,
      cefr: ['C2'],
      description: 'Sophisticated spoken signposting',
    }),
    lesson({
      id: 'nuanced-stance',
      name: 'Nuanced Stance',
      difficulty: 5,
      cefr: ['C2'],
      description: 'Qualify opinions with precision',
    }),
    lesson({
      id: 'native-like-repair',
      name: 'Native-like Repair',
      difficulty: 5,
      cefr: ['C2'],
      description: 'Self-correct without losing fluency',
    }),
    lesson({
      id: 'basic-pronunciation',
      name: 'Basic Pronunciation',
      difficulty: 1,
      preIelts: true,
      description: 'Sounds, stress and clarity',
    }),
    lesson({
      id: 'using-examples',
      name: 'Using Examples Effectively',
      difficulty: 3,
      description: 'Support answers with examples',
    }),
    lesson({
      id: 'abstract-topics',
      name: 'Abstract Topics',
      difficulty: 5,
      academic: true,
      description: 'Discuss ideas in Part 3',
    }),
  ],
};

export const READING_TESTS = [
  { id: 1, name: 'Academic Reading 1', path: '/exams/english/reading', difficulty: 1, format: 'academic' },
  { id: 2, name: 'Academic Reading 2', path: '/exams/english/reading', difficulty: 1, format: 'academic' },
  { id: 3, name: 'General Reading 1', path: '/exams/english/reading', difficulty: 1, format: 'general' },
  { id: 4, name: 'General Reading 2', path: '/exams/english/reading', difficulty: 2, format: 'general' },
  { id: 5, name: 'Academic Reading 3', path: '/exams/english/reading', difficulty: 2, format: 'academic' },
  { id: 6, name: 'General Reading 3', path: '/exams/english/reading', difficulty: 2, format: 'general' },
  { id: 7, name: 'Academic Reading 4', path: '/exams/english/reading', difficulty: 2, format: 'academic' },
  { id: 8, name: 'General Reading 4', path: '/exams/english/reading', difficulty: 3, format: 'general' },
  { id: 9, name: 'Academic Reading 5', path: '/exams/english/reading', difficulty: 3, format: 'academic' },
  { id: 10, name: 'General Reading 5', path: '/exams/english/reading', difficulty: 3, format: 'general' },
  { id: 11, name: 'Academic Reading 6', path: '/exams/english/reading', difficulty: 3, format: 'academic' },
  { id: 12, name: 'General Reading 6', path: '/exams/english/reading', difficulty: 3, format: 'general' },
  { id: 13, name: 'Academic Reading 7', path: '/exams/english/reading', difficulty: 4, format: 'academic' },
  { id: 14, name: 'General Reading 7', path: '/exams/english/reading', difficulty: 4, format: 'general' },
  { id: 15, name: 'Academic Reading 8', path: '/exams/english/reading', difficulty: 4, format: 'academic' },
  { id: 16, name: 'General Reading 8', path: '/exams/english/reading', difficulty: 4, format: 'general' },
  { id: 17, name: 'Academic Reading 9', path: '/exams/english/reading', difficulty: 5, format: 'academic' },
  { id: 18, name: 'Academic Reading 10', path: '/exams/english/reading', difficulty: 5, format: 'academic' },
];

export const WRITING_TESTS = [
  { id: 1, name: 'Task 1: Charts', path: '/exams/english/writing', difficulty: 1, format: 'academic' },
  { id: 2, name: 'Task 2: Essay', path: '/exams/english/writing', difficulty: 1, format: 'academic' },
  { id: 3, name: 'Task 1: Maps', path: '/exams/english/writing', difficulty: 2, format: 'academic' },
  { id: 4, name: 'Task 2: Opinion', path: '/exams/english/writing', difficulty: 2, format: 'academic' },
  { id: 5, name: 'Task 1: Process', path: '/exams/english/writing', difficulty: 3, format: 'academic' },
  { id: 6, name: 'Task 2: Discussion', path: '/exams/english/writing', difficulty: 3, format: 'academic' },
  { id: 7, name: 'Task 1: Tables', path: '/exams/english/writing', difficulty: 4, format: 'academic' },
  { id: 8, name: 'Task 2: Problem', path: '/exams/english/writing', difficulty: 4, format: 'academic' },
  { id: 9, name: 'Task 1: Diagrams', path: '/exams/english/writing', difficulty: 5, format: 'academic' },
  { id: 10, name: 'Task 2: Two-Part Question', path: '/exams/english/writing', difficulty: 5, format: 'academic' },
];

export const LISTENING_TESTS = [
  { id: 1, name: 'Conversation Practice', path: '/exams/english/listening', difficulty: 1, format: 'general' },
  { id: 2, name: 'Lecture Practice', path: '/exams/english/listening', difficulty: 1, format: 'academic' },
  { id: 3, name: 'Monologue Practice', path: '/exams/english/listening', difficulty: 2, format: 'general' },
  { id: 4, name: 'Discussion Practice', path: '/exams/english/listening', difficulty: 2, format: 'general' },
  { id: 5, name: 'Academic Lecture', path: '/exams/english/listening', difficulty: 3, format: 'academic' },
  { id: 6, name: 'Social Context', path: '/exams/english/listening', difficulty: 3, format: 'general' },
  { id: 7, name: 'Academic Discussion', path: '/exams/english/listening', difficulty: 4, format: 'academic' },
  { id: 8, name: 'General Training', path: '/exams/english/listening', difficulty: 4, format: 'general' },
  { id: 9, name: 'Advanced Lecture', path: '/exams/english/listening', difficulty: 5, format: 'academic' },
  { id: 10, name: 'Professional Context', path: '/exams/english/listening', difficulty: 5, format: 'academic' },
];

const MOCK_CEFR_MIN = {
  1: 'B2',
  2: 'C1',
  3: 'B2',
  4: 'B2',
  5: 'C1',
};

export const MOCK_EXAMS = mockExamData.map((exam) => ({
  ...exam,
  cefrMin: exam.cefrMin || MOCK_CEFR_MIN[exam.id] || 'B2',
}));

const CEFR_RANK = { A1: 0, A2: 1, B1: 2, B2: 3, C1: 4, C2: 5 };

export const getMocksForCefr = (levelCode) => {
  const rank = CEFR_RANK[levelCode] ?? 2;
  return MOCK_EXAMS.filter((exam) => (CEFR_RANK[exam.cefrMin] ?? 3) <= rank);
};

export const LIBRARY_GRAMMAR_LESSONS = GRAMMAR_LESSONS.map((item) => ({
  id: item.id,
  title: item.name,
  description: item.description,
  path: item.path,
}));

export const LIBRARY_VOCABULARY_LESSONS = VOCABULARY_LESSONS.map((item) => ({
  id: item.id,
  title: item.name,
  description: item.description,
  path: item.path,
}));

export const getAllCatalogLessons = () => [
  ...GRAMMAR_LESSONS,
  ...VOCABULARY_LESSONS,
  ...IELTS_SKILLS_COURSES.listening,
  ...IELTS_SKILLS_COURSES.reading,
  ...IELTS_SKILLS_COURSES.writing,
  ...IELTS_SKILLS_COURSES.speaking,
];

export const getLessonsForCefr = (levelCode, items) => {
  const rank = CEFR_RANK[levelCode] ?? 2;
  return items.filter((item) => {
    if (Array.isArray(item.cefr) && item.cefr.length > 0) {
      return item.cefr.includes(levelCode);
    }
    if (item.academic && rank < CEFR_RANK.B1) {
      return false;
    }
    if (levelCode === 'A1' || levelCode === 'A2') {
      return item.preIelts || item.difficulty <= (levelCode === 'A1' ? 1 : 2);
    }
    return true;
  });
};

export const validateIeltsCatalog = (allCourseData = {}) => {
  const issues = [];
  const lessons = getAllCatalogLessons();
  const seenIds = new Set();
  const pathOwners = new Map();

  lessons.forEach((item) => {
    if (seenIds.has(item.id)) {
      issues.push({ type: 'duplicate-id', id: item.id });
    }
    seenIds.add(item.id);

    if (!allCourseData[item.id] && !allCourseData[item.courseId]) {
      issues.push({ type: 'missing-course-data', id: item.id });
    }

    const isDynamic = item.path.startsWith('/exams/english/lesson/');
    if (!isDynamic) {
      const owner = pathOwners.get(item.path);
      if (owner && owner !== item.id) {
        issues.push({
          type: 'shared-path',
          id: item.id,
          path: item.path,
          otherId: owner,
        });
      } else {
        pathOwners.set(item.path, item.id);
      }
    }
  });

  return { issues, lessonCount: lessons.length };
};
